/**
 * React hook for the Optimist connection toggle on the Profile page.
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useState } from "react";
import { ToastMessages } from "../components/toasts/toastMessages";
import {
  callOptimistConnection,
  type OptimistConnectionResult,
} from "../services/optimistConnectionService";

/** Toast for a failed request, picking the session-expired message for a non-code 401. */
const toastFailure = (result: OptimistConnectionResult) =>
  toast.error(
    result.status === 401
      ? ToastMessages.OPTIMIST_SESSION_EXPIRED
      : ToastMessages.OPTIMIST_CONNECTION_ERROR,
  );

/**
 * Loads, enables (with an authenticator code), and disables the user's Optimist connection.
 *
 * Status is fetched even when the facility has the feature off, so a connected user can revoke.
 *
 * @returns Connection state (`connected` is null until loaded) and handlers
 */
export function useOptimistConnection() {
  const [connected, setConnected] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaNotEnrolled, setMfaNotEnrolled] = useState(false);
  const [codeError, setCodeError] = useState("");

  useEffect(() => {
    let cancelled = false;
    callOptimistConnection("GET").then((result) => {
      if (cancelled) return;
      if (result.enabled !== undefined) setConnected(result.enabled);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Enable the connection after the user confirms with their current authenticator code.
   *
   * @param code - Current 6-digit authenticator code
   * @returns `true` when the connection was enabled
   */
  const handleEnable = useCallback(async (code: string) => {
    setLoading(true);
    setCodeError("");
    const result = await callOptimistConnection("POST", code);
    setLoading(false);

    if (result.enabled !== undefined) {
      setConnected(result.enabled);
      toast.success(ToastMessages.OPTIMIST_CONNECTION_ENABLED);
      return true;
    }
    if (result.detail === "mfa_not_enrolled") {
      setMfaNotEnrolled(true);
    } else if (result.detail === "Invalid MFA code") {
      setCodeError(
        "Invalid or already used code. Wait for a new one and try again.",
      );
    } else if (result.status === 429) {
      setCodeError(result.detail ?? ToastMessages.OPTIMIST_CONNECTION_ERROR);
    } else {
      toastFailure(result);
    }
    return false;
  }, []);

  /** Disable the connection. */
  const handleDisable = useCallback(async () => {
    setLoading(true);
    const result = await callOptimistConnection("DELETE");
    setLoading(false);

    if (result.enabled !== undefined) {
      setConnected(result.enabled);
      toast.success(ToastMessages.OPTIMIST_CONNECTION_DISABLED);
    } else {
      toastFailure(result);
    }
  }, []);

  return {
    connected,
    loading,
    mfaNotEnrolled,
    codeError,
    handleEnable,
    handleDisable,
  };
}
